/**
 * NFT fetching and management
 * Handles retrieving Metaplex NFTs from a wallet via Helius DAS API
 */

import { Connection, PublicKey } from "@solana/web3.js";
import { getAssociatedTokenAddressSync, TOKEN_PROGRAM_ID } from "@solana/spl-token";

/**
 * Represents an NFT that can be burned
 */
export interface NFTInfo {
  /** The NFT mint address */
  mint: PublicKey;
  /** The token account holding the NFT */
  tokenAccount: PublicKey;
  /** NFT name from metadata */
  name: string;
  /** NFT image URL */
  image: string;
  /** Collection address (if part of a collection) */
  collectionAddress: PublicKey | null;
  /** Collection name (if available) */
  collectionName: string | null;
}

/**
 * Response from the Helius assets API
 */
interface HeliusNFTResponse {
  success: boolean;
  nfts?: Array<{
    mint: string;
    name: string;
    image: string;
    collectionAddress: string | null;
    collectionName: string | null;
    tokenAccount: string | null;
  }>;
  total?: number;
  error?: string;
  source?: string;
}

/**
 * Fetch all NFTs owned by a wallet
 * Uses Helius DAS API via our proxy route
 * Excludes compressed NFTs (cNFTs)
 *
 * @param connection - Solana RPC connection (used for deriving token accounts if needed)
 * @param owner - Wallet public key
 * @param skipCache - If true, bypass server-side cache (use after burns)
 * @returns Array of NFT info objects
 */
export async function fetchNFTs(
  connection: Connection,
  owner: PublicKey,
  skipCache: boolean = false
): Promise<NFTInfo[]> {
  const ownerAddress = owner.toBase58();

  try {
    // Determine the base URL for the API
    const baseUrl =
      typeof window !== "undefined"
        ? window.location.origin
        : "http://localhost:3000";

    const cacheParam = skipCache ? "?skipCache=true" : "";
    const response = await fetch(`${baseUrl}/api/helius-assets/${ownerAddress}${cacheParam}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.error(`[nfts] API error: ${response.status}`);
      return [];
    }

    const data: HeliusNFTResponse = await response.json();

    if (!data.success || !data.nfts) {
      console.error("[nfts] API returned error:", data.error);
      return [];
    }

    // Transform API response to NFTInfo objects
    const nfts: NFTInfo[] = [];

    for (const nft of data.nfts) {
      try {
        const mint = new PublicKey(nft.mint);

        // Derive or use provided token account
        let tokenAccount: PublicKey;
        if (nft.tokenAccount) {
          tokenAccount = new PublicKey(nft.tokenAccount);
        } else {
          // Derive the associated token account
          tokenAccount = getAssociatedTokenAddressSync(mint, owner, false, TOKEN_PROGRAM_ID);
        }

        nfts.push({
          mint,
          tokenAccount,
          name: nft.name,
          image: nft.image,
          collectionAddress: nft.collectionAddress
            ? new PublicKey(nft.collectionAddress)
            : null,
          collectionName: nft.collectionName,
        });
      } catch (error) {
        console.error(`[nfts] Error processing NFT ${nft.mint}:`, error);
        // Skip invalid NFTs
      }
    }

    // Sort by collection name (if any), then by name
    nfts.sort((a, b) => {
      if (a.collectionName && b.collectionName) {
        const collCompare = a.collectionName.localeCompare(b.collectionName);
        if (collCompare !== 0) return collCompare;
      }
      if (a.collectionName && !b.collectionName) return -1;
      if (!a.collectionName && b.collectionName) return 1;
      return a.name.localeCompare(b.name);
    });

    return nfts;
  } catch (error) {
    console.error("[nfts] Error fetching NFTs:", error);
    return [];
  }
}
